/**
 * @file Picks the Analytics page's "Last computed" value: the earliest
 * `builtAt` across every build of the answered results on screen, with that
 * build's `cutTime` and its result's `snapshotId`. Consumer: the page's "Last
 * computed" text. Loading, refused and failed answers carry no builds and are
 * skipped. When no answer on screen read a build, as on Reports and Metrics,
 * the caller's own home summary's builds are read the same way; `null` means
 * neither read one.
 */
import type { Snapshot } from 'valtio';

import type { AnalyticsSummary, FactBuildProvenance } from '#types';

import type { AnalyticsAnswer, AnalyticsLastComputed } from './index.types';

const earlier = (
  a: AnalyticsLastComputed | null,
  b: AnalyticsLastComputed | null,
): AnalyticsLastComputed | null =>
  a === null || (b !== null && Date.parse(b.builtAt) < Date.parse(a.builtAt))
    ? b
    : a;

const earliestOf = (
  builds: readonly Snapshot<FactBuildProvenance>[],
  snapshotId: number,
): AnalyticsLastComputed | null =>
  builds
    .map(({ builtAt, cutTime }) => ({ builtAt, cutTime, snapshotId }))
    .reduce<AnalyticsLastComputed | null>(earlier, null);

export const analyticsLastComputed = (
  answers: readonly (Snapshot<AnalyticsAnswer> | undefined)[],
  summary: Snapshot<Pick<AnalyticsSummary, 'builds' | 'snapshotId'>> | undefined,
): AnalyticsLastComputed | null =>
  answers
    .map((answer) =>
      answer?.status === 'answered'
        ? earliestOf(answer.result.builds, answer.result.snapshotId)
        : null,
    )
    .reduce(earlier, null) ??
  (summary ? earliestOf(summary.builds, summary.snapshotId) : null);
